import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as apiService from '../../services/apiService';
import { EmailRecordDetail, EmailRecordSummary, Page } from '../../types';
import { RootState } from '../store';
import { forceRefreshAccounts } from './accountsSlice';

type AsyncStatus = 'idle' | 'loading' | 'loadingMore' | 'succeeded' | 'failed';

interface EmailsState {
  emails: EmailRecordSummary[];
  currentPage: number;
  hasMore: boolean;
  status: AsyncStatus;
  error: string | null;
  detailsById: Record<number, EmailRecordDetail>;
  detailStatusById: Record<number, Exclude<AsyncStatus, 'loadingMore'>>;
  detailErrorById: Record<number, string | null>;
  reprocessStatusById: Record<number, Exclude<AsyncStatus, 'loadingMore'>>;
  reprocessErrorById: Record<number, string | null>;
}

interface FetchEmailsArgs {
  page?: number;
  size?: number;
  forceRefresh?: boolean;
}

const initialState: EmailsState = {
  emails: [],
  currentPage: -1,
  hasMore: true,
  status: 'idle',
  error: null,
  detailsById: {},
  detailStatusById: {},
  detailErrorById: {},
  reprocessStatusById: {},
  reprocessErrorById: {},
};

const mergeEmails = (existing: EmailRecordSummary[], incoming: EmailRecordSummary[]): EmailRecordSummary[] => {
  const mergedById = new Map<number, EmailRecordSummary>();
  existing.forEach((email) => mergedById.set(email.id, email));
  incoming.forEach((email) => mergedById.set(email.id, email));

  return Array.from(mergedById.values()).sort((a, b) => {
    const aTime = new Date(a.receivedAt).getTime();
    const bTime = new Date(b.receivedAt).getTime();
    return bTime - aTime;
  });
};

export const fetchEmails = createAsyncThunk<
  Page<EmailRecordSummary>,
  FetchEmailsArgs | void,
  { state: RootState; rejectValue: string }
>(
  'emails/fetchEmails',
  async (args, { rejectWithValue }) => {
    try {
      const page = args?.page ?? 0;
      const size = args?.size ?? 20;
      return await apiService.fetchEmails(page, size);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch emails';
      return rejectWithValue(message);
    }
  },
  {
    condition: (args, { getState }) => {
      const state = getState() as RootState;
      const requestedPage = args?.page ?? 0;
      const { status, currentPage, hasMore } = state.emails;
      const isFetching = status === 'loading' || status === 'loadingMore';

      if (args?.forceRefresh) {
        return !isFetching;
      }

      if (isFetching || status === 'failed') {
        return false;
      }
      if (!hasMore && requestedPage > currentPage) {
        return false;
      }
      if (requestedPage <= currentPage) {
        return false;
      }
      return true;
    },
  }
);

export const fetchEmailById = createAsyncThunk<
  EmailRecordDetail,
  number | { emailId: number; forceRefresh?: boolean },
  { state: RootState; rejectValue: string }
>(
  'emails/fetchEmailById',
  async (args, { rejectWithValue, dispatch }) => {
    try {
      const emailId = typeof args === 'number' ? args : args.emailId;
      const email = await apiService.fetchEmailById(emailId);
      if (email.processingStatus === 'PROCESSED') {
        try {
          await dispatch(forceRefreshAccounts()).unwrap();
        } catch (refreshError) {
          console.error('Failed to refresh accounts after email processing:', refreshError);
        }
      }
      return email;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch email';
      return rejectWithValue(message);
    }
  },
  {
    condition: (args, { getState }) => {
      const state = getState() as RootState;
      const emailId = typeof args === 'number' ? args : args.emailId;
      const forceRefresh = typeof args === 'object' && args.forceRefresh;
      const status = state.emails.detailStatusById[emailId];
      if (status === 'loading') {
        return false;
      }
      if (forceRefresh) {
        return true;
      }
      return !state.emails.detailsById[emailId];
    },
  }
);

export const reprocessEmail = createAsyncThunk<
  EmailRecordDetail,
  number,
  { state: RootState; rejectValue: string }
>(
  'emails/reprocessEmail',
  async (emailId, { rejectWithValue }) => {
    try {
      return await apiService.reprocessEmail(emailId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to reprocess email';
      return rejectWithValue(message);
    }
  },
  {
    condition: (emailId, { getState }) => {
      const state = getState() as RootState;
      return state.emails.reprocessStatusById[emailId] !== 'loading';
    },
  }
);

const emailsSlice = createSlice({
  name: 'emails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state, action) => {
        const requestedPage = action.meta.arg?.page ?? 0;
        state.status = requestedPage > 0 ? 'loadingMore' : 'loading';
        state.error = null;
      })
      .addCase(fetchEmails.fulfilled, (state, action: PayloadAction<Page<EmailRecordSummary>>) => {
        const pageData = action.payload;
        state.status = 'succeeded';
        state.emails = mergeEmails(state.emails, pageData.content);
        state.currentPage = pageData.number;
        state.hasMore = !pageData.last;
      })
      .addCase(fetchEmails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = typeof action.payload === 'string'
          ? action.payload
          : action.error.message ?? 'Failed to fetch emails';
      })
      .addCase(fetchEmailById.pending, (state, action) => {
        const id = typeof action.meta.arg === 'number' ? action.meta.arg : action.meta.arg.emailId;
        state.detailStatusById[id] = 'loading';
        state.detailErrorById[id] = null;
      })
      .addCase(fetchEmailById.fulfilled, (state, action: PayloadAction<EmailRecordDetail>) => {
        const email = action.payload;
        state.detailsById[email.id] = email;
        state.emails = state.emails.map((summary) =>
          summary.id === email.id
            ? {
                ...summary,
                processingStatus: email.processingStatus,
                transactionsCreated: email.transactionsCreated,
                parsedTextPreview: email.parsedTextBody ?? summary.parsedTextPreview,
              }
            : summary
        );
        state.detailStatusById[email.id] = 'succeeded';
        state.detailErrorById[email.id] = null;
      })
      .addCase(fetchEmailById.rejected, (state, action) => {
        const id = typeof action.meta.arg === 'number' ? action.meta.arg : action.meta.arg.emailId;
        state.detailStatusById[id] = 'failed';
        state.detailErrorById[id] = typeof action.payload === 'string'
          ? action.payload
          : action.error.message ?? 'Failed to fetch email';
      })
      .addCase(reprocessEmail.pending, (state, action) => {
        const id = action.meta.arg;
        state.reprocessStatusById[id] = 'loading';
        state.reprocessErrorById[id] = null;
      })
      .addCase(reprocessEmail.fulfilled, (state, action: PayloadAction<EmailRecordDetail>) => {
        const email = action.payload;
        state.detailsById[email.id] = email;
        state.emails = state.emails.map((summary) =>
          summary.id === email.id
            ? {
                ...summary,
                processingStatus: email.processingStatus,
                transactionsCreated: email.transactionsCreated,
              }
            : summary
        );
        state.reprocessStatusById[email.id] = 'succeeded';
        state.reprocessErrorById[email.id] = null;
      })
      .addCase(reprocessEmail.rejected, (state, action) => {
        const id = action.meta.arg;
        state.reprocessStatusById[id] = 'failed';
        state.reprocessErrorById[id] = typeof action.payload === 'string'
          ? action.payload
          : action.error.message ?? 'Failed to reprocess email';
      });
  },
});

export default emailsSlice.reducer;
